import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BarChart3,
  DollarSign,
  ShoppingBag,
  Store,
  TrendingUp,
  Wallet,
} from "lucide-react";

import {
  locationSalesGetOrderTypeReportData,
  locationSalesGetSalesTotals,
  locationsGetAllMine,
} from "~/api-refs";
import { StatCard } from "~/components/reports/stat-card";
import { toConvexTableId } from "~/lib/convex-table-id";

const formatCurrency = (value: number | undefined) =>
  value?.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  }) ?? "$0.00";

export function CompleteDashboardStep() {
  const { data: locations } = useQuery(convexQuery(locationsGetAllMine, {}));

  const firstLocation = locations?.[0];

  // Today's date range
  const today = new Date();
  const startOfDay = new Date(today);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);

  const dateRange = {
    from: startOfDay.getTime(),
    to: endOfDay.getTime(),
  };

  const { data: salesTotals } = useQuery(
    convexQuery(
      locationSalesGetSalesTotals,
      firstLocation
        ? {
            dateRange,
            locationId: toConvexTableId("locations", firstLocation.id),
          }
        : "skip"
    )
  );

  const { data: orderTypes } = useQuery(
    convexQuery(
      locationSalesGetOrderTypeReportData,
      firstLocation
        ? {
            dateRange,
            locationId: toConvexTableId("locations", firstLocation.id),
          }
        : "skip"
    )
  );

  return (
    <>
      {/* Today's Sales */}
      <div>
        <div className="mb-4 flex items-baseline justify-between">
          <h3 className="subtitle-sm text-foreground">Today</h3>
          {firstLocation && (
            <p className="body-sm text-muted-foreground">
              {firstLocation.name}
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatCard
            icon={DollarSign}
            title="Gross Sales"
            value={formatCurrency(salesTotals?.grossSales)}
          />
          <StatCard
            icon={TrendingUp}
            title="Net Sales"
            value={formatCurrency(salesTotals?.netSales)}
          />
          <StatCard
            icon={ShoppingBag}
            title="Orders"
            value={salesTotals?.ordersCount?.toLocaleString("en-US") ?? "0"}
          />
        </div>
      </div>

      {/* Order Type Breakdown */}
      {orderTypes && orderTypes.length > 0 && (
        <div>
          <h3 className="subtitle-sm text-foreground mb-4">Orders by Type</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {orderTypes.map((entry) => (
              <div
                className="border-border bg-background rounded-xl border p-5"
                key={entry.type}
              >
                <p className="label text-muted-foreground">{entry.type}</p>
                <p className="h3 text-foreground mt-2">
                  {entry.itemsSold} items
                </p>
                <p className="body-sm text-muted-foreground mt-1">
                  {formatCurrency(entry.amountSold)} &middot;{" "}
                  {Math.round(entry.shareOfTotalSales)}% of sales
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Link to="/balances">
          <div className="group border-border bg-background hover:bg-muted/40 flex items-center gap-4 rounded-xl border p-5 transition-colors">
            <div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
              <Wallet className="text-primary size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground font-medium">Balance</p>
              <p className="body-sm text-muted-foreground">View payouts</p>
            </div>
            <ArrowRight className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
        <Link
          search={{
            type: undefined,
            location: undefined,
            groupBy: undefined,
          }}
          to="/reports"
        >
          <div className="group border-border bg-background hover:bg-muted/40 flex items-center gap-4 rounded-xl border p-5 transition-colors">
            <div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
              <BarChart3 className="text-primary size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground font-medium">Reports</p>
              <p className="body-sm text-muted-foreground">Sales & analytics</p>
            </div>
            <ArrowRight className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
        <Link to="/locations">
          <div className="group border-border bg-background hover:bg-muted/40 flex items-center gap-4 rounded-xl border p-5 transition-colors">
            <div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
              <Store className="text-primary size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-foreground font-medium">Locations</p>
              <p className="body-sm text-muted-foreground">Manage locations</p>
            </div>
            <ArrowRight className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </Link>
      </div>
    </>
  );
}
